// Constraints of the analyst on the category order of one attribute (todo/10): pins and blocks.
//
// A pin fixes one category at a position of its axis. A block is a contiguous sequence of categories with a fixed
// inner order; the block as a whole is free to move. A fully fixed order (ordinal attribute, in the direction the
// analyst chose) is the block over all categories; blocks supersede it. The admissible orders of the attribute are
// the orders that satisfy the pins and keep every block.
//
// Structure: the categories are grouped into units (a block or a single category). A unit with a pinned category
// has a fixed start position; the other units (free units) fill the remaining positions from left to right, so
// an admissible order is a sequence of the free units that fits into the gaps.
import { randomInt, randomPermutation } from './rng.js';

/**
 * @param categories  category names of the attribute in base order
 * @param constraint  { order: [names] (all categories, display order), pins: { name: position },
 *                      blocks: [[names], ...] (display order within a block) }
 * @returns structure, or null if the attribute is unconstrained
 */
export function buildStructure(categories, { order = null, pins = null, blocks = null } = {}) {
    const n = categories.length;
    const index = name => {
        const i = categories.indexOf(name);
        if (i < 0) throw new Error('Unknown category: ' + name);
        return i;
    };
    if (order && !(blocks && blocks.length)) {
        if (order.length !== n) throw new Error('Category order is not a permutation of the base order.');
        blocks = [order];
    }
    const unitOf = new Int32Array(n).fill(-1);
    const units = [];
    for (const block of blocks || []) {
        if (block.length < 2) continue;
        const unit = Int32Array.from(block, index);
        for (const c of unit) {
            if (unitOf[c] >= 0) throw new Error('Category in two blocks: ' + categories[c]);
            unitOf[c] = units.length;
        }
        units.push(unit);
    }
    const pinned = Object.entries(pins || {});
    if (units.length === 0 && pinned.length === 0) return null;
    for (let c = 0; c < n; c++) if (unitOf[c] < 0) { unitOf[c] = units.length; units.push(Int32Array.of(c)); }

    // Start position of the units with a pinned category.
    const start = new Int32Array(units.length).fill(-1);
    for (const [name, position] of pinned) {
        const c = index(name), u = unitOf[c];
        const s = position - units[u].indexOf(c);
        if (!Number.isInteger(position) || s < 0 || s + units[u].length > n) throw new Error('Pin out of range: ' + name);
        if (start[u] >= 0 && start[u] !== s) throw new Error('Pins contradict a block: ' + name);
        start[u] = s;
    }
    // occupied[p]: pinned unit that starts at p (-1: none); taken[p]: position belongs to a pinned unit.
    const occupied = new Int32Array(n).fill(-1), taken = new Uint8Array(n);
    const freeUnits = [];
    units.forEach((unit, u) => {
        if (start[u] < 0) { freeUnits.push(u); return; }
        occupied[start[u]] = u;
        for (let p = start[u]; p < start[u] + unit.length; p++) {
            if (taken[p]) throw new Error('Pins contradict each other at position ' + p + '.');
            taken[p] = 1;
        }
    });
    const structure = { n, units, unitOf, freeUnits, occupied, taken, all: null };
    if (!firstOrder(structure)) throw new Error('No category order satisfies the constraints of the attribute.');
    return structure;
}

// Position array of a sequence of the free units, or null if a block does not fit into its gap.
export function layout(structure, sequence) {
    const { n, units, occupied, taken } = structure;
    const pos = new Int32Array(n);
    let p = 0, next = 0;
    while (p < n) {
        let unit;
        if (occupied[p] >= 0) {
            unit = units[occupied[p]];
        } else {
            if (next === sequence.length) return null;
            unit = units[sequence[next++]];
            if (p + unit.length > n) return null;
            for (let q = p; q < p + unit.length; q++) if (taken[q]) return null;
        }
        for (let o = 0; o < unit.length; o++) pos[unit[o]] = p + o;
        p += unit.length;
    }
    return next === sequence.length ? pos : null;
}

// Free units of an order in display order.
function sequenceOf(structure, pos) {
    return structure.freeUnits.slice().sort((a, b) => pos[structure.units[a][0]] - pos[structure.units[b][0]]);
}

// Admissible orders as position arrays, at most limit; free units are tried in base order.
function enumerate(structure, limit = Infinity) {
    const { freeUnits } = structure;
    const list = [], sequence = [], used = new Array(freeUnits.length).fill(false);
    const recurse = () => {
        if (list.length >= limit) return;
        if (sequence.length === freeUnits.length) {
            const pos = layout(structure, sequence);
            if (pos) list.push(pos);
            return;
        }
        for (let i = 0; i < freeUnits.length; i++) {
            if (used[i]) continue;
            used[i] = true; sequence.push(freeUnits[i]);
            if (prefixFits(structure, sequence)) recurse();
            used[i] = false; sequence.pop();
        }
    };
    recurse();
    return list;
}

// True if the free units placed so far fit into the gaps (prunes the enumeration).
function prefixFits(structure, sequence) {
    const { n, units, occupied, taken } = structure;
    let p = 0;
    for (const u of sequence) {
        while (p < n && occupied[p] >= 0) p += units[occupied[p]].length;
        const length = units[u].length;
        if (p + length > n) return false;
        for (let q = p; q < p + length; q++) if (taken[q]) return false;
        p += length;
    }
    return true;
}

export function firstOrder(structure) {
    return structure.all ? structure.all[0] : enumerate(structure, 1)[0] || null;
}

// All admissible orders (cached); grows with the factorial of the number of free units.
export function admissibleOrders(structure) {
    if (!structure.all) structure.all = enumerate(structure);
    return structure.all;
}

// True if more than n category orders are admissible; stops the enumeration after n + 1 orders.
export function moreAdmissibleThan(structure, n) {
    return (structure.all || enumerate(structure, n + 1)).length > n;
}

// True if a position array satisfies the constraints.
export function isAdmissible(structure, pos) {
    const expected = layout(structure, sequenceOf(structure, pos));
    return expected !== null && expected.every((p, c) => p === pos[c]);
}

// Uniform over the sequences of the free units; with blocks next to pins a sequence may not fit and is redrawn.
export function randomOrder(structure, rand) {
    const { freeUnits } = structure;
    for (let attempt = 0; attempt < 100; attempt++) {
        const pos = layout(structure, Array.from(randomPermutation(rand, freeUnits.length), i => freeUnits[i]));
        if (pos) return pos;
    }
    const all = admissibleOrders(structure);
    return all[randomInt(rand, all.length)];
}

// Neighbors of an admissible order: the moves of local-search.js on the sequence of the free units, so that a
// block moves as one unit and the pinned categories stay in place; sequences that do not fit are dropped.
export function constrainedNeighbors(structure, pos, neighborhood) {
    if (neighborhood === 'block') return admissibleOrders(structure);
    const sequence = sequenceOf(structure, pos);
    const m = sequence.length;
    const list = [];
    const add = next => { const p = layout(structure, next); if (p) list.push(p); };
    const swap = (i, j) => { const next = sequence.slice(); next[i] = sequence[j]; next[j] = sequence[i]; add(next); };
    if (neighborhood === 'adjacentSwap') {
        for (let i = 0; i + 1 < m; i++) swap(i, i + 1);
    } else if (neighborhood === 'swap') {
        for (let i = 0; i < m; i++) for (let j = i + 1; j < m; j++) swap(i, j);
    } else if (neighborhood === 'insert') {
        for (let i = 0; i < m; i++) {
            for (let j = 0; j < m; j++) {
                if (j === i || j === i - 1) continue;
                const next = sequence.slice();
                next.splice(j, 0, next.splice(i, 1)[0]);
                add(next);
            }
        }
    } else {
        throw new Error('Unknown neighborhood: ' + neighborhood);
    }
    return list;
}
